import gql from 'graphql-tag'

export const QUIZ_RETAKE_BLOCKED_QUERY = gql`
  query quizRetakeBlocked($quizSlug: String!, $email: String!) {
    quizRetakeBlocked(quizSlug: $quizSlug, email: $email)
  }
`

export const ASK_BOWVUE_QUERY = gql`
  query askBowvue($question: String!, $locationId: ID, $history: [AskTurn!]) {
    askBowvue(question: $question, locationId: $locationId, history: $history) {
      answer
      sources {
        documentId
        name
        citedText
        link
      }
    }
  }
`

export const ALL_PARENT_LOCATIONS_QUERY = gql`
  query parentLocation(
    $first: Int!
    $offset: Int!
    $filter: LocationFilter!
    $sort: SortLocationArgs!
  ) {
    parentLocations(
      first: $first
      offset: $offset
      filter: $filter
      sort: $sort
    ) {
      edges {
        node {
          id
          name
        }
      }
      pageInfo {
        hasNextPage
        totalItems
      }
    }
  }
`

export const LOCATION_REGULAR_DEVICES_QUERY = gql`
  query regularDevicesByLocation(
    $first: Int!
    $offset: Int!
    $filter: DeviceFilter!
    $sort: SortDeviceArgs
  ) {
    regularDevicesByLocation(
      first: $first
      offset: $offset
      filter: $filter
      sort: $sort
    ) {
      edges {
        node {
          id
          name
          type
          conditionOfMaintenance {
            conditionOfMaintenanceID
            description
            shortDescription
            deviceID
          }
          incidentEnergySurvey {
            incidentEnergy
            arcFlashBoundary
          }
          settings {
            voltage
          }
        }
      }
      pageInfo {
        hasNextPage
        totalItems
      }
    }
  }
`

export const LOCATION_PROTECTIVE_DEVICES_QUERY = gql`
  query protectiveDevicesByLocation(
    $first: Int!
    $offset: Int!
    $filter: DeviceFilter!
    $sort: SortDeviceArgs
  ) {
    protectiveDevicesByLocation(
      first: $first
      offset: $offset
      filter: $filter
      sort: $sort
    ) {
      edges {
        node {
          id
          name
          type
          conditionOfMaintenance {
            conditionOfMaintenanceID
            description
            shortDescription
            deviceID
          }
          incidentEnergySurvey {
            incidentEnergy
            arcFlashBoundary
          }
          settings {
            voltage
            frameSize
            plugSize
            kaic
            manufacturer
            type
            model
          }
        }
      }
      pageInfo {
        hasNextPage
        totalItems
      }
    }
  }
`

export const ADMIN_USER_QUERY = gql`
  query allUsers($filter: UserFilter!) {
    allUsers(filter: $filter, first: 1) {
      edges {
        node {
          id
          email
          firstName
          lastName
          status
          enabled
          roles
          company
          title
          accountLocations {
            id
            name
            parentLocation {
              id
            }
          }
        }
      }
    }
  }
`

export const USERS_BY_LOCATION = gql`
  query usersByLocation(
    $first: Int!
    $offset: Int!
    $locationID: ID!
    $sort: SortUserArgs
  ) {
    usersByLocation(
      first: $first
      offset: $offset
      locationID: $locationID
      sort: $sort
    ) {
      edges {
        node {
          id
          email
          firstName
          lastName
          status
          enabled
          roles
          permissions
        }
      }
      pageInfo {
        hasNextPage
        totalItems
      }
    }
  }
`

export const ALL_INFRARED_DEVICES_QUERY = gql`
  query devicesByLocation(
    $first: Int!
    $offset: Int!
    $filter: DeviceFilter!
    $sort: SortDeviceArgs
  ) {
    devicesByLocation(
      first: $first
      offset: $offset
      filter: $filter
      sort: $sort
    ) {
      edges {
        node {
          id
          name
          tag
          type
          enabled
          updatedAt
          location {
            id
            name
            __typename
          }
          incidentEnergySurvey {
            incidentEnergy
            arcFlashBoundary
            __typename
          }
          settings {
            voltage
            voltageType
            manufacturer
            floor
            room
            areaLocation
            building
            amperage
            type
            hasArcFlashStudy
            __typename
          }
          infraredSurvey {
            id
            date
            status
            description
            updatedAt
            device {
              id
            }
            operator {
              id
              email
              firstName
              lastName
              __typename
            }
            observations {
              id
              type
              fixed
              recommendations
              description
              priority
              notes
              referenceTemperature
              spotTemperature
              measuredCurrentPhaseA
              measuredCurrentPhaseB
              measuredCurrentPhaseC
              millivoltDropPhaseA
              millivoltDropPhaseB
              millivoltDropPhaseC
              updatedAt
              photos {
                id
                name
                size
                faultPhotoLink
              }
            }
            codeViolations {
              id
              type
              fixed
              shortDescription
              codeViolationID
              recommendations
              notes
              description
              updatedAt
              photos {
                id
                name
                size
                faultPhotoLink
              }
            }
            photos {
              id
              name
              size
              surveyPhotoLink
            }
            __typename
          }
          __typename
        }
      }
      pageInfo {
        hasNextPage
        totalItems
        __typename
      }
      __typename
    }
  }
`

export const DASHBOARD_LOCATIONS_QUERY = gql`
  query mapLocations($filter: LocationFilter!, $first: Int!, $offset: Int!) {
    locations(filter: $filter, first: $first, offset: $offset) {
      edges {
        node {
          id
          name
          city
          state
          latitude
          longitude
        }
      }
      pageInfo {
        hasNextPage
        totalItems
      }
    }
  }
`

// Facilities for the scheduler's "Facility" pickers. Deliberately lean: just
// enough to list, search, and choose a site.
//
// ADMIN_ALL_LOCATIONS_QUERY can't do this job. It selects a dozen fields plus a
// nested parentLocation, and `allLocations` costs (first / 20) x the child
// selection under the server's FixedComplexityLimit(1000). So it has to ask for
// a few hundred rows at a time. With ~2,500 sites on file that silently returned
// a fraction of them, and the scheduler then dropped the disabled ones from
// THAT page, leaving most facilities unreachable and unsearchable. Four scalars
// per node keeps the cost low enough to page through every site.
//
// `status` is filtered SERVER-side (LocationFilter.status). Filtering it on the
// client after paging is what compounded the truncation.
export const SCHEDULABLE_LOCATIONS_QUERY = gql`
  query allLocations(
    $filter: LocationFilter!
    $first: Int!
    $offset: Int!
    $sort: SortLocationArgs!
  ) {
    allLocations(filter: $filter, first: $first, offset: $offset, sort: $sort) {
      edges {
        node {
          id
          name
          city
          state
        }
      }
      pageInfo {
        hasNextPage
        totalItems
      }
    }
  }
`

export const ADMIN_ALL_LOCATIONS_QUERY = gql`
  query allLocations(
    $filter: LocationFilter!
    $first: Int!
    $offset: Int!
    $sort: SortLocationArgs!
  ) {
    allLocations(filter: $filter, first: $first, offset: $offset, sort: $sort) {
      edges {
        node {
          id
          name
          streetAddress
          secondaryAddress
          city
          state
          zipCode
          latitude
          longitude
          status
          # OFFLINE-SYNC: uncomment when the backend adds Location.updatedAt to
          # enable offline conflict detection (see
          # docs/offline-sync-backend-contract.md). editLocation/enable/disable/
          # reset already pass it as baseVersion. No other frontend change.
          # updatedAt
          parentLocation {
            id
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        totalItems
      }
    }
  }
`

export const LOCATIONS_QUERY = gql`
  query locations(
    $filter: LocationFilter!
    $first: Int!
    $offset: Int!
    $sort: SortLocationArgs!
  ) {
    locations(filter: $filter, first: $first, offset: $offset, sort: $sort) {
      edges {
        node {
          id
          name
          streetAddress
          secondaryAddress
          city
          state
          zipCode
          latitude
          longitude
          status
          parentLocation {
            id
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        totalItems
      }
    }
  }
`

export const ADMIN_ALL_USERS_QUERY = gql`
  query allUsers(
    $filter: UserFilter!
    $first: Int!
    $offset: Int!
    $sort: SortUserArgs!
  ) {
    allUsers(filter: $filter, first: $first, offset: $offset, sort: $sort) {
      edges {
        node {
          id
          email
          firstName
          lastName
          status
          enabled
          roles
        }
      }
      pageInfo {
        hasNextPage
        totalItems
      }
    }
  }
`

// Lean user list for the Infrared Offline operator dropdown. Deliberately
// selects NO Cognito-backed fields (enabled/status/createdAt/lastModifiedAt):
// serving those costs the backend one rate-limited AdminGetUser call PER USER
// (~7s for the full list), which was most of the offline download's wait. The
// backend skips that fan-out when none of them are requested.
export const OPERATORS_QUERY = gql`
  query allUsers(
    $filter: UserFilter!
    $first: Int!
    $offset: Int!
    $sort: SortUserArgs!
  ) {
    allUsers(filter: $filter, first: $first, offset: $offset, sort: $sort) {
      edges {
        node {
          id
          email
          firstName
          lastName
        }
      }
      pageInfo {
        hasNextPage
        totalItems
      }
    }
  }
`

export const DEVICE_QUERY = gql`
  query device($id: ID!) {
    device(id: $id) {
      id
      name
      # OFFLINE-SYNC: uncomment when the backend adds Device.updatedAt
      # (offline conflict detection, changeDevice passes it as baseVersion).
      # updatedAt
      conditionOfMaintenance {
        conditionOfMaintenanceID
        shortDescription
        description
        deviceID
        # OFFLINE-SYNC: uncomment when the backend adds
        # ConditionOfMaintenance.updatedAt (condition-of-maintenance edits pass
        # it as baseVersion).
        # updatedAt
      }
      settings {
        voltage
        voltageType
        type
        floor
        room
        manufacturer
        phase
        amperage
        kva
        serialNumber
        # A protective device's model and ratings, as a saved arc flash report
        # records them: the details card shows each one stored.
        model
        frameSize
        sensorSize
        kaic
      }
      incidentEnergySurvey {
        incidentEnergy
        arcFlashBoundary
      }
      gloveClass
      location {
        id
        name
      }
      documents {
        id
        name
        link
        size
        upload_date
      }
      lotoDocuments {
        id
        name
        link
        size
        upload_date
      }
      infraredSurvey {
        id
        date
        status
        # The reason an INACCESSIBLE survey was inaccessible, which the surveyor
        # is required to enter. device.vue renders it behind a v-if, so without
        # it the panel is indistinguishable from a survey with no note at all.
        description
        device {
          id
        }
        operator {
          id
          email
          firstName
          lastName
          __typename
        }
        observations {
          id
          type
          fixed
          recommendations
          description
          notes
          referenceTemperature
          spotTemperature
          measuredCurrentPhaseA
          measuredCurrentPhaseB
          measuredCurrentPhaseC
          millivoltDropPhaseA
          millivoltDropPhaseB
          millivoltDropPhaseC
          photos {
            id
            name
            size
            faultPhotoLink
          }
        }
        codeViolations {
          id
          type
          fixed
          shortDescription
          codeViolationID
          recommendations
          notes
          description
          photos {
            id
            name
            size
            faultPhotoLink
          }
        }
        photos {
          id
          name
          size
          surveyPhotoLink
        }
        __typename
      }
      technicians {
        id
        name
        title
        phoneNumber
        level
        voltageMax
        incidentEnergy
        isDefault
      }
    }
  }
`

// A location's technicians, the fixed company default plus its assigned ones,
// for the location page's management card. Devices inherit these: a device
// lists its location's technicians.
export const TECHNICIANS_BY_LOCATION_QUERY = gql`
  query techniciansByLocation($locationID: ID!) {
    techniciansByLocation(locationID: $locationID) {
      id
      name
      title
      phoneNumber
      level
      voltageMax
      incidentEnergy
      isDefault
    }
  }
`

// How many locations sit beneath one, at any depth. Its own query rather than a
// field on the admin list: descendantCount is a recursive walk per location, and
// only the disable dialog needs it. For one location, on open.
export const LOCATION_DESCENDANT_COUNT_QUERY = gql`
  query location($id: ID!) {
    location(id: $id) {
      id
      descendantCount
    }
  }
`

export const LOCATION_QUERY = gql`
  query location($id: ID!) {
    location(id: $id) {
      id
      name
      streetAddress
      secondaryAddress
      city
      state
      zipCode
      latitude
      longitude
      parentLocation {
        id
        name
      }
      lastArcFlashStudy
      lastInfraredStudy
      logo {
        logoLink
      }
      documents {
        id
        name
        link
        size
        upload_date
      }
      trainingDocuments {
        id
        name
        link
        size
        upload_date
      }
      projectDocuments {
        id
        name
        link
        size
        upload_date
      }
      # Documents and Training files archived here, newest archive first.
      archivedDocuments {
        id
        name
        link
        size
        upload_date
        archivedAt
        archivedByName
      }
    }
  }
`

export const DEVICE_METRICS_QUERY = gql`
  query deviceMetrics($locations: [ID!]) {
    deviceMetrics(locations: $locations) {
      totalDevices
      totalBuses
      levelOne
      levelTwo
      levelZero
      levelThree
      levelFour
    }
  }
`

export const DOCUMENT_UPLOAD_QUERY = gql`
  query uploadLinkDocument {
    uploadLinkDocument {
      storeId
      url
    }
  }
`

export const ALL_CODE_VIOLATIONS_QUERY = gql`
  query allCodeViolations {
    allCodeViolations {
      edges {
        node {
          id
          description
          shortDescription
          recommendations
        }
      }
    }
  }
`

export const ALL_SUPPORT_TICKETS_QUERY = gql`
  query allSupportTickets(
    $first: Int!
    $offset: Int!
    $filter: SupportTicketFilter!
    $sort: SortSupportTicketArgs
  ) {
    allSupportTickets(
      first: $first
      offset: $offset
      filter: $filter
      sort: $sort
    ) {
      edges {
        node {
          id
          name
          email
          status
          message
          company
          createdAt
          updatedAt
        }
      }
      pageInfo {
        hasNextPage
        totalItems
      }
    }
  }
`
export const ALL_CONDITION_OF_MAINTENANCE_QUERY = gql`
  query allConditionOfMaintenances(
    $first: Int!
    $offset: Int!
    $sort: SortConditionOfMaintanencesArgs
  ) {
    allConditionOfMaintenances(first: $first, offset: $offset, sort: $sort) {
      edges {
        node {
          id
          shortDescription
          description
        }
      }
    }
  }
`
// $location is nullable on purpose: the location page passes a site id, the
// dashboard passes null to roll the condition breakdown up across every location
// the user can read (mirrors deviceMetrics' all-accessible-locations aggregate).
// The infrared counts for one location's infrared card, or, with $location null,
// the dashboard's roll-up across every location the user can read. Read access to
// the location is all it takes, as for the condition breakdown beside it.
export const INFRARED_METRICS_QUERY = gql`
  query infraredMetrics($location: ID) {
    infraredMetrics(location: $location) {
      devices
      devicesSurveyed
      surveys
      faults
      openFaults
    }
  }
`

export const CONDITION_OF_MAINTENANCE_METRICS = gql`
  query conditionOfMaintenanceMetrics($location: ID) {
    conditionOfMaintenanceMetrics(location: $location) {
      totalDevices
      levelZero
      levelOne
      levelTwo
      levelThree
      levelFour
    }
  }
`

// Lists submitted attempts for one training quiz, newest first, for the
// admin/developer results page. Server-side counterpart is net-new (same
// backend work as SUBMIT_QUIZ_ATTEMPT_MUTATION). The backend must implement
// `quizAttempts(quizSlug: String!): [QuizAttempt!]!` reading the table that
// submitQuizAttempt writes to. Date filtering is done client-side on
// `submittedAt` (an ISO 8601 timestamp).
// The certificate audit history: each generated batch, newest first, with
// everything done with it (downloaded, emailed, filed under a location).
export const CERTIFICATE_BATCHES_QUERY = gql`
  query certificateBatches(
    $filter: CertificateBatchFilter
    $first: Int
    $offset: Int
  ) {
    certificateBatches(filter: $filter, first: $first, offset: $offset) {
      totalCount
      items {
        id
        family
        familyLabel
        course
        company
        certificateDate
        hours
        nfpaYear
        certificateCount
        names
        paperNames
        resultsFrom
        resultsTo
        zipFilename
        zipBytes
        zipSha256
        generatedByName
        generatedByEmail
        generatedAt
        automatic
        events {
          id
          kind
          succeeded
          error
          emailTo
          emailCc
          emailSubject
          locationId
          locationName
          documentId
          byName
          byEmail
          occurredAt
        }
      }
    }
  }
`

// Whether a passing exam emails its certificate automatically, per exam family
// (2026-10-23). Training Results shows one switch per family.
export const TRAINING_AUTO_CERTIFICATES_QUERY = gql`
  query trainingAutoCertificates {
    trainingAutoCertificates {
      family
      enabled
      updatedByName
      updatedAt
    }
  }
`

export const QUIZ_ATTEMPTS_QUERY = gql`
  query quizAttempts($quizSlug: String!) {
    quizAttempts(quizSlug: $quizSlug) {
      id
      quizSlug
      name
      email
      company
      score
      passed
      totalQuestions
      correctCount
      submittedAt
      answers {
        questionId
        choiceId
      }
      # The result email the taker was sent (2026-10-27), without the message:
      # QUIZ_RESULT_EMAIL_QUERY reads it whole. Null before then.
      resultEmail {
        id
        to
        subject
        sentAt
        sent
        error
        attachmentName
      }
    }
  }
`

// One attempt's result email as it was sent, the message included.
export const QUIZ_RESULT_EMAIL_QUERY = gql`
  query quizResultEmail($quizSlug: String!, $attemptId: ID!) {
    quizResultEmail(quizSlug: $quizSlug, attemptId: $attemptId) {
      id
      to
      subject
      sentAt
      sent
      error
      attachmentName
      certificateBatchId
      text
      html
    }
  }
`

export const SCHEDULABLE_USERS_QUERY = gql`
  query schedulableUsers {
    schedulableUsers {
      id
      email
      firstName
      lastName
    }
  }
`

export const SCHEDULE_ASSIGNMENTS_QUERY = gql`
  query scheduleAssignments($startDate: String!, $endDate: String!) {
    scheduleAssignments(startDate: $startDate, endDate: $endDate) {
      id
      userId
      date
      kind
      status
      workTypes
      # Qualifies ARC_FLASH ("Arc Flash - Modeling"). Selected here as well as
      # written: without it the board renders a plain "Arc Flash" and the editor
      # reopens the entry with the stage radios blank, which reads as the save
      # having lost it.
      arcFlashStage
      locationId
      locationName
      locationPlace
      notes
      jobGroupId
    }
  }
`

// Add someone to the scheduler's staff list, or take them off it. Removing does
// NOT delete their assignments. The board simply stops listing them.
export const SET_USER_SCHEDULABLE_MUTATION = gql`
  mutation setUserSchedulable($input: SetUserSchedulableInput!) {
    setUserSchedulable(input: $input)
  }
`

// Candidates for the scheduler's "add someone" picker. The search term goes to
// the SERVER (UserFilter.email) and the field re-queries as you type, so a page
// size here can't hide anyone. The trap that left the facility picker showing
// 138 of 1,635 sites.
export const SCHEDULABLE_CANDIDATES_QUERY = gql`
  query allUsers(
    $filter: UserFilter!
    $first: Int!
    $offset: Int!
    $sort: SortUserArgs!
  ) {
    allUsers(filter: $filter, first: $first, offset: $offset, sort: $sort) {
      edges {
        node {
          id
          email
          firstName
          lastName
        }
      }
    }
  }
`

// A job's PDF attachments (by group id), each with a presigned inline-open URL.
export const SCHEDULE_ATTACHMENTS_QUERY = gql`
  query scheduleAttachments($jobGroupId: String!) {
    scheduleAttachments(jobGroupId: $jobGroupId) {
      id
      name
      downloadUrl
    }
  }
`

// The signed-in user's OWN upcoming assignments (self-scoped, not admin-gated).
// Powers the "My Schedule" card on the profile page. Returns [] for users who
// aren't scheduled.
export const MY_SCHEDULE_ASSIGNMENTS_QUERY = gql`
  query myScheduleAssignments($startDate: String!, $endDate: String!) {
    myScheduleAssignments(startDate: $startDate, endDate: $endDate) {
      id
      date
      kind
      status
      workTypes
      # Qualifies ARC_FLASH ("Arc Flash - Modeling"). Selected here as well as
      # written: without it the board renders a plain "Arc Flash" and the editor
      # reopens the entry with the stage radios blank, which reads as the save
      # having lost it.
      arcFlashStage
      locationId
      locationName
      locationPlace
      notes
      # The day-rows of one multi-day entry share this, so the card can collapse
      # a span into a single row.
      jobGroupId
    }
  }
`

// Who else is on site with the signed-in user, per one of their own job days
// (same location, same day, a job's group id is per-person, so it can't link
// people). Kept as its own query, not fields on the assignment, so the My
// Schedule card still renders if the deployed API predates it.
export const MY_SCHEDULE_CREW_QUERY = gql`
  query myScheduleCrew($startDate: String!, $endDate: String!) {
    myScheduleCrew(startDate: $startDate, endDate: $endDate) {
      assignmentId
      members {
        id
        firstName
        lastName
        email
      }
    }
  }
`

// The signed-in user's in-app notifications (bell dropdown), newest first.
export const NOTIFICATIONS_QUERY = gql`
  query notifications($limit: Int) {
    notifications(limit: $limit) {
      id
      type
      title
      body
      # Where clicking it goes: an in-app path, or null (@utils/notification-link).
      link
      read
      createdAt
    }
  }
`

// Unread count for the bell badge (cheap; polled).
export const UNREAD_NOTIFICATION_COUNT_QUERY = gql`
  query unreadNotificationCount {
    unreadNotificationCount
  }
`

// LOTO's own equipment list for a site (backend migration 00063).
//
// This replaced regularDevicesByLocation. The 60k-row asset register shared
// with infrared and arc flash. The field set was settled against three real
// customer spreadsheets: only the NAME appears in all of them, so it is the
// only required field.
//
// Not paginated: a site's LOTO list is authored by hand or imported once, so it
// is small by construction. lotoProcedures is still resolved per device, so the
// page's "Procedures" column costs a round trip per row.
export const LOTO_DEVICES_QUERY = gql`
  query lotoDevices($locationID: ID!) {
    lotoDevices(locationID: $locationID) {
      id
      locationId
      name
      equipmentLocation
      equipmentId
      lotoProcedures {
        id
        type
        status
        updatedAt
        operator {
          id
          firstName
          lastName
          email
        }
        # Only the ids: Start lockout counts the control points (a complex
        # lockout needs two) and tells a draft from an empty placeholder.
        steps {
          id
        }
      }
    }
  }
`

// The LOTO Offline download: the full authored tree for a location, devices,
// every procedure, every step, step photos, plus the updatedAt versions the
// offline conflict detection sends back as expectedUpdatedAt. Separate from
// LOTO_DEVICES_QUERY (the online list), which deliberately omits steps.
export const LOTO_OFFLINE_DEVICES_QUERY = gql`
  query lotoOfflineDevices($locationID: ID!) {
    lotoDevices(locationID: $locationID) {
      id
      locationId
      name
      equipmentLocation
      equipmentId
      updatedAt
      # When its procedures last changed, as downloaded: what an Add Procedure
      # made offline is checked against at sync, so it never replaces changes
      # made online meanwhile without asking (keep mine or discard).
      proceduresChangedAt
      # The equipment's own photo, which the procedure card shows offline too.
      photo {
        name
        link
      }
      # Everything the procedure card reads (LOTO_PROCEDURES_QUERY), so offline
      # shows and edits the same procedure as online (2026-10-23).
      lotoProcedures {
        id
        deviceId
        type
        status
        finalizedAt
        finalizedBy
        details
        otherHazards {
          type
          notes
          labels
        }
        updatedAt
        operator {
          id
          firstName
          lastName
          email
        }
        steps {
          id
          procedureId
          stepNumber
          description
          action
          info
          verification
          controlPoint
          controlPointName
          lockoutEquipment
          actionFields {
            key
            value
          }
          updatedAt
          photos {
            id
            stepId
            name
            size
            link
            shapes {
              type
              color
              x1
              y1
              x2
              y2
            }
          }
        }
      }
    }
  }
`

// Location LOTO readiness summary. One aggregate resolver rather than resolving
// every device's procedures per row, which does not scale to sites with thousands
// of LOTO devices. "Written" (covered) means a procedure with at least one step;
// the backend excludes the empty procedure stub the app creates when an editor is
// opened. lackingEquipment is the flagged list rendered under the donut.
// $locationID is nullable on purpose: the location page passes a site id, the
// dashboard passes null to roll readiness up across every location the user can
// read (admin/agent-only, matching the query's @hasRole).
export const LOTO_LOCATION_SUMMARY_QUERY = gql`
  query lotoReadiness($locationID: ID) {
    lotoReadiness(locationID: $locationID) {
      equipmentCount
      procedureCount
      equipmentWithProcedures
      equipmentLacking
      averageProceduresPerEquipment
      lackingEquipment {
        id
        name
        equipmentLocation
      }
    }
  }
`

// Text already written in one step field, offered again while typing in that
// same field ("Tur" offers "Turn off equipment"). From every site the person can
// read, never further; text from deviceID's site first. See the schema.
export const LOTO_STEP_TEXT_SUGGESTIONS_QUERY = gql`
  query lotoStepTextSuggestions(
    $field: LotoStepTextField!
    $term: String!
    $deviceID: ID
    $first: Int
  ) {
    lotoStepTextSuggestions(
      field: $field
      term: $term
      deviceID: $deviceID
      first: $first
    )
  }
`

// The photo a marked-up control point photo was drawn on, so editing the control
// point can redraw its marks on it. Null when no original was kept.
export const LOTO_STEP_PHOTO_ORIGINAL_QUERY = gql`
  query lotoStepPhotoOriginal($id: ID!) {
    lotoStepPhotoOriginal(id: $id)
  }
`

// The equipment's own photo, from the Add Procedure wizard, for the procedure card.
// Fetched uncached on its own: the link is presigned and expires, and a photo that
// cannot be had is no reason to hold up the procedures.
export const LOTO_DEVICE_PHOTO_QUERY = gql`
  query lotoDevicePhoto($id: ID!) {
    lotoDevice(id: $id) {
      id
      photo {
        name
        link
      }
    }
  }
`

// The whole equipment's procedure history, every energy type, newest first. A
// procedure the Add Procedure wizard replaced comes with its snapshot: its text and
// its photos as they were.
export const LOTO_DEVICE_PROCEDURE_HISTORY_QUERY = gql`
  query lotoDeviceProcedureHistory($deviceID: ID!) {
    lotoDeviceProcedureHistory(deviceID: $deviceID) {
      id
      action
      energyType
      occurredAt
      # When it was made on the device, for a change made offline and synced
      # later (occurredAt is then when it arrived). See @utils/made-at.
      madeAt
      actor
      changes {
        field
        from
        to
      }
      snapshot {
        status
        details
        otherHazards {
          type
          notes
          labels
        }
        steps {
          stepNumber
          description
          action
          info
          verification
          photos {
            name
            link
          }
        }
      }
    }
  }
`

export const LOTO_PROCEDURES_QUERY = gql`
  query lotoProcedures($deviceID: ID!) {
    lotoProcedures(deviceID: $deviceID) {
      id
      deviceId
      type
      status
      finalizedAt
      finalizedBy
      details
      otherHazards {
        type
        notes
        labels
      }
      steps {
        id
        procedureId
        stepNumber
        description
        action
        info
        verification
        controlPoint
        controlPointName
        lockoutEquipment
        actionFields {
          key
          value
        }
        photos {
          id
          stepId
          name
          size
          link
          shapes {
            type
            color
            x1
            y1
            x2
            y2
          }
        }
      }
    }
  }
`

// The combined Study Renewals list: one row per location, both studies side by
// side. `studiesNeeded` is what the chips read from, the day counts cannot carry
// it, because a study that is CURRENT and one that has NEVER been done both report
// 0 days overdue.
// Whether askBowvue is available to the signed-in user, so the widget is not
// rendered for someone whose every question would be refused.
//
// PRESENTATION ONLY, the same check is enforced inside askBowvue itself. Hiding the
// widget hides a button, not the query.
export const ASK_BOWVUE_AVAILABLE_QUERY = gql`
  query askBowvueAvailable {
    askBowvueAvailable
  }
`

export const LOCATION_STUDY_RENEWALS_QUERY = gql`
  query locationStudyRenewals(
    $first: Int!
    $offset: Int!
    $name: String
    $sortDirection: SortDirection
  ) {
    locationStudyRenewals(
      first: $first
      offset: $offset
      name: $name
      sortDirection: $sortDirection
    ) {
      edges {
        location {
          id
          name
          city
          state
        }
        lastArcFlashStudy
        lastInfraredStudy
        studiesNeeded
        arcFlashDaysOverdue
        infraredDaysOverdue
        arcFlashDaysUntilDue
        infraredDaysUntilDue
        contacted
        lastContact {
          id
          locationId
          contactedOn
          method
          outcome
          nextStep
          studies
          recordedBy
          recordedAt
          updatedAt
        }
        rootLocationId
        rootLocationName
      }
      pageInfo {
        totalItems
        hasNextPage
      }
    }
  }
`

// One site's contact log from the Study Renewals panel, newest first.
export const STUDY_RENEWAL_CONTACTS_QUERY = gql`
  query studyRenewalContacts($locationId: ID!) {
    studyRenewalContacts(locationId: $locationId) {
      id
      locationId
      contactedOn
      method
      outcome
      nextStep
      studies
      recordedBy
      recordedAt
      updatedAt
    }
  }
`

// Devices for the admin devices page. Deliberately NOT ALL_INFRARED_DEVICES_QUERY,
// which drags in every survey, observation, code violation and photo for each device.
// this page shows a table and an edit form, so it asks for the fields those need and
// nothing else.
//
// `enabled` is the point of the page: devicesByLocation is unfiltered, so a disabled
// device is still listed and can be re-enabled. A query that hid them would make
// disabling a one-way door.
export const ADMIN_DEVICES_QUERY = gql`
  query devicesByLocation(
    $first: Int!
    $offset: Int!
    $filter: DeviceFilter!
    $sort: SortDeviceArgs
  ) {
    devicesByLocation(
      first: $first
      offset: $offset
      filter: $filter
      sort: $sort
    ) {
      edges {
        node {
          id
          name
          tag
          type
          enabled
          settings {
            voltage
            voltageType
            manufacturer
            amperage
            floor
            room
            # READ BACK BY device-edit-dialog AND WRITTEN OUT AGAIN on save.
            # Without them the dialog's draft starts empty and the save clears
            # what was there: areaLocation and building drop out of the
            # variables as undefined, but hasArcFlashStudy is double-negated to
            # a boolean, so it goes out as an explicit FALSE and wipes the flag
            # that says an arc flash study exists.
            areaLocation
            building
            hasArcFlashStudy
            __typename
          }
          __typename
        }
        __typename
      }
      pageInfo {
        totalItems
        __typename
      }
      __typename
    }
  }
`

// ─── Legacy Infrared archive ────────────────────────────────────────────────
// Read-only views over the retired Rails application's records, which live in a
// separate database and share no identity space with Bowvue. All of it is
// admin-gated server-side; LEGACY_IR_AVAILABLE_QUERY is the switch the page uses
// to hide itself when the archive isn't configured for an environment.

export const LEGACY_IR_AVAILABLE_QUERY = gql`
  query legacyIRAvailable {
    legacyIRAvailable
  }
`

export const LEGACY_IR_ORGANIZATIONS_QUERY = gql`
  query legacyIROrganizations(
    $first: Int!
    $offset: Int!
    $filter: LegacyIRFilter
  ) {
    legacyIROrganizations(first: $first, offset: $offset, filter: $filter) {
      items {
        id
        name
        locationCount
        deviceCount
        surveyCount
        lastSurveyAt
        __typename
      }
      totalItems
      __typename
    }
  }
`

export const LEGACY_IR_LOCATIONS_QUERY = gql`
  query legacyIRLocations(
    $first: Int!
    $offset: Int!
    $filter: LegacyIRFilter
  ) {
    legacyIRLocations(first: $first, offset: $offset, filter: $filter) {
      items {
        id
        name
        city
        state
        deviceCount
        surveyCount
        lastSurveyAt
        __typename
      }
      totalItems
      __typename
    }
  }
`

export const LEGACY_IR_DEVICES_QUERY = gql`
  query legacyIRDevices($first: Int!, $offset: Int!, $filter: LegacyIRFilter) {
    legacyIRDevices(first: $first, offset: $offset, filter: $filter) {
      items {
        id
        name
        deviceType
        voltage
        phase
        amperage
        manufacturer
        room
        floor
        surveyCount
        findingCount
        lastSurveyAt
        __typename
      }
      totalItems
      __typename
    }
  }
`

export const LEGACY_IR_SURVEYS_QUERY = gql`
  query legacyIRSurveys($first: Int!, $offset: Int!, $filter: LegacyIRFilter) {
    legacyIRSurveys(first: $first, offset: $offset, filter: $filter) {
      items {
        id
        date
        status
        operator
        deviceId
        deviceName
        locationId
        locationName
        organizationName
        photoCount
        findingCount
        __typename
      }
      totalItems
      __typename
    }
  }
`

// The detail query is the only one that pulls photos, because every photo field
// costs a presigned-URL round trip to S3.
export const LEGACY_IR_SURVEY_QUERY = gql`
  query legacyIRSurvey($id: String!) {
    legacyIRSurvey(id: $id) {
      id
      date
      status
      operator
      deviceName
      locationName
      organizationName
      photoCount
      findingCount
      photos {
        id
        fileName
        camera
        link
        thumbnailLink
        __typename
      }
      findings {
        id
        type
        fixed
        legacyViolationCode
        description
        recommendations
        notes
        referenceTemperature
        spotTemperature
        measuredCurrentPhaseA
        measuredCurrentPhaseB
        measuredCurrentPhaseC
        millivoltDropPhaseA
        millivoltDropPhaseB
        millivoltDropPhaseC
        photoCount
        photos {
          id
          fileName
          link
          thumbnailLink
          __typename
        }
        __typename
      }
      __typename
    }
  }
`

// The checklist a technician is about to work through for one device. Built by
// the server on demand and NOT stored: nothing exists until it is signed.
//
// `fingerprint` identifies this exact list of steps and is passed back when
// signing. A procedure edited while the checklist was open is then refused
// rather than silently shifting which text the signature covers, so it is not
// optional to select it.
export const LOTO_LOCKOUT_CHECKLIST_QUERY = gql`
  query lotoLockoutChecklist(
    $deviceID: ID!
    $language: ReportLanguage
    # The control points THIS person is taking from a complex lockout's board.
    # Omitted for a simple lockout, which is one person taking the whole machine.
    $controlPoints: [ID!]
    # Whole hazards: what was sent before lockouts were split by control point.
    $energyTypes: [String!]
  ) {
    lotoLockoutChecklist(
      deviceID: $deviceID
      language: $language
      controlPoints: $controlPoints
      energyTypes: $energyTypes
    ) {
      deviceId
      deviceName
      deviceEquipmentId
      deviceEquipmentLocation
      fingerprint
      steps {
        id
        section
        energyType
        stepNumber
        displayOrder
        description
        detail
        # The procedure step an isolation row came from: its photo is looked up
        # by it.
        sourceStepId
      }
    }
  }
`

// One machine's lockout history, newest first. Not paginated: it is read in
// full, on the device's own page.
export const LOTO_LOCKOUTS_QUERY = gql`
  query lotoLockouts($deviceID: ID!) {
    lotoLockouts(deviceID: $deviceID) {
      id
      deviceId
      # Needed when a release has to be queued: the outbox tags items by
      # location so a per-location sync can drain them. Without it a queued
      # release is untagged and that sync never sees it.
      locationId
      status
      deviceName
      deviceEquipmentId
      deviceEquipmentLocation
      appliedSignatureName
      # The drawn mark, a PNG data URL. Empty on every record signed before drawn
      # signatures existed, which is why the history renders it conditionally.
      appliedSignatureImage
      appliedAt
      # When it was signed on the device, for a signature made offline and
      # synced later (appliedAt is then when it arrived). See @utils/made-at.
      appliedMadeAt
      equipmentState
      otherPersonnel
      releasedSignatureName
      releasedSignatureImage
      releasedAt
      releasedMadeAt
      releaseOverrideReason
      # Why a lockout was closed WITHOUT anyone signing for it. Empty on every
      # normally released record; separate from releaseOverrideReason, which is
      # about a substitute releaser.
      releaseNote
      appliedBy {
        id
        firstName
        lastName
        email
      }
      releasedBy {
        id
        firstName
        lastName
        email
      }
      steps {
        id
        section
        energyType
        stepNumber
        displayOrder
        description
        detail
        checkedAt
      }
    }
  }
`

// Just enough to render one location in a picker: its id and its name.
//
// LOCATION_QUERY exists but drags documents, coordinates and study dates along
// with it, which is a lot to fetch so an autocomplete can show a name. Used when
// a page knows WHICH location is selected (from the URL, a QR deep link, a
// reload) but the picker does not.
export const LOCATION_NAME_QUERY = gql`
  query location($id: ID!) {
    location(id: $id) {
      id
      name
    }
  }
`

// The machine's live lockout and its per-hazard board, or null when nobody is
// locked onto it.
//
// `fullyCovered` is the ONLY thing that may be read as "this machine is locked
// out". It is false whenever any required hazard is uncovered, and the banner
// turns on it rather than on the lockout merely existing.
export const LOTO_LOCKOUT_GROUP_QUERY = gql`
  query lotoLockoutGroup($deviceID: ID!) {
    lotoLockoutGroup(deviceID: $deviceID) {
      id
      mode
      requiredEnergyTypes
      fullyCovered
      uncoveredEnergyTypes
      openedAt
      openedBy {
        id
        firstName
        lastName
      }
      holds {
        id
        energyType
        # The control point this hold is (2026-10-23); blank label and no
        # step on a hold that stands for a whole hazard.
        stepId
        pointLabel
        pointNumber
        covered
        plannedUser {
          id
          firstName
          lastName
          # The fallback when a profile carries no name. Plenty of accounts have
          # only an email, and without this the board said "Assigned to " and
          # then nothing at all.
          email
        }
        event {
          id
          status
          appliedSignatureName
          appliedAt
          appliedMadeAt
          appliedBy {
            id
            firstName
            lastName
            email
          }
        }
      }
    }
  }
`

// How many lockouts a piece of LOTO equipment has on record. With any at all it
// cannot be deleted (the records are safety evidence), so it is archived instead;
// the remove dialog asks before offering one or the other.
export const LOTO_DEVICE_LOCKOUT_COUNT_QUERY = gql`
  query lotoDeviceLockoutCount($id: ID!) {
    lotoDevice(id: $id) {
      id
      lockoutCount
    }
  }
`

// A site's archived LOTO equipment, by name: what lotoDevices leaves out, to be
// restored.
export const LOTO_ARCHIVED_DEVICES_QUERY = gql`
  query lotoArchivedDevices($locationID: ID!) {
    lotoArchivedDevices(locationID: $locationID) {
      id
      name
      equipmentLocation
      equipmentId
      archivedAt
    }
  }
`

// The role permission grid (admin Role Permissions page): every module and its
// actions, and a row per role. Admins and developers.
export const ROLE_PERMISSIONS_QUERY = gql`
  query rolePermissions {
    permissionCatalog {
      key
      enforced
      actions
    }
    rolePermissions {
      role
      siteScope
      permissions
      userCount
      editable
      builtin
      updatedAt
      updatedBy
    }
  }
`

// Changes to the grid, newest first.
export const ROLE_PERMISSION_HISTORY_QUERY = gql`
  query rolePermissionHistory($first: Int) {
    rolePermissionHistory(first: $first) {
      id
      role
      action
      permission
      fromScope
      toScope
      changedBy
      changedAt
    }
  }
`

// Every role a user can be given, in BowVue's order: DEVELOPER, ADMIN, the
// built-in roles, then the ones added from the Role Permissions page. Admins
// and developers only, like giving roles.
export const ASSIGNABLE_ROLES_QUERY = gql`
  query assignableRoles {
    assignableRoles
  }
`

// The activity trail (bowvue-graphql package activity): what was done, who did
// it and when. A location's has everything done there and on its devices; a
// device's, only its own. Needs activity.read at the site.
export const LOCATION_ACTIVITY_QUERY = gql`
  query locationActivity($locationId: ID!, $first: Int, $offset: Int) {
    locationActivity(locationId: $locationId, first: $first, offset: $offset) {
      items {
        id
        action
        details
        actorName
        deviceId
        createdAt
        madeAt
      }
      hasMore
    }
  }
`

export const DEVICE_ACTIVITY_QUERY = gql`
  query deviceActivity($deviceId: ID!, $first: Int, $offset: Int) {
    deviceActivity(deviceId: $deviceId, first: $first, offset: $offset) {
      items {
        id
        action
        details
        actorName
        deviceId
        createdAt
        madeAt
      }
      hasMore
    }
  }
`

// The DEVELOPER-only Server Logs page: this API server's most recent log entries,
// newest first, from its in-memory buffer (redacted on the server).
export const SERVER_LOGS_QUERY = gql`
  query serverLogs($level: ServerLogLevel, $search: String, $limit: Int) {
    serverLogs(level: $level, search: $search, limit: $limit) {
      matched
      retained
      capacity
      startedAt
      entries {
        seq
        time
        level
        message
        caller
        fields {
          key
          value
        }
      }
    }
  }
`

// A location's arc flash report project (Admin, Arc Flash Report): the details
// last used for its report, or ones started from the location.
export const ARC_FLASH_REPORT_PROJECT_QUERY = gql`
  query arcFlashReportProject($locationId: ID!) {
    arcFlashReportProject(locationId: $locationId) {
      locationId
      saved
      updatedAt
      updatedByName
      reportId
      generatedAt
      reportUrl
      reportExpired
      details {
        customerName
        projectNumber
        reportDate
        dataCollectionDate
        softwareVersion
        siteAddress {
          name
          street
          city
          state
          zipCode
        }
        utilities {
          name
          provided
          meterNumber
          primaryVoltage
          secondaryVoltage
          xr
          xfmrKva
          mva
        }
        revisions {
          date
          note
        }
        recommendations {
          priority
          description
          notes
        }
        conclusions {
          heading
          text
        }
      }
    }
  }
`

// A device's arc flash label as it prints (the device's page): the image, a
// data: URL, or why it has none. Not cached: the image is a few dozen
// kilobytes, and it changes when the label is printed (its QR code).
export const DEVICE_ARC_FLASH_LABEL_QUERY = gql`
  query deviceArcFlashLabel($deviceId: ID!) {
    deviceArcFlashLabel(deviceId: $deviceId) {
      available
      reason
      image
    }
  }
`

// ---------------- who uses BowVue and when (Developers only) ----------------
// bowvue-graphql schema/presence.gql, migration 00108. The API refuses everyone
// but Developers, admins included.

// Who was on in the last few minutes. Polled while the Users page is open; the
// API does not count this query as the viewer being on.
export const ACTIVE_USERS_QUERY = gql`
  query activeUsers($minutes: Int) {
    activeUsers(minutes: $minutes) {
      userId
      name
      email
      roles
      lastActiveAt
      device
    }
  }
`

// When each person on a page of the Users table was last on.
export const USERS_LAST_ACTIVE_QUERY = gql`
  query usersLastActive($userIds: [ID!]!) {
    usersLastActive(userIds: $userIds) {
      userId
      lastActiveAt
      device
    }
  }
`

// One person's use of BowVue, for their page.
export const USER_ACTIVITY_SUMMARY_QUERY = gql`
  query userActivitySummary($userId: ID!, $timeZone: String!) {
    userActivitySummary(userId: $userId, timeZone: $timeZone) {
      userId
      lastActiveAt
      device
      activeDays
      minutesToday
    }
  }
`

// Who was on during a day, window by window.
export const PRESENCE_DAY_QUERY = gql`
  query presenceDay($date: String!, $timeZone: String!) {
    presenceDay(date: $date, timeZone: $timeZone) {
      recordedSince
      people {
        userId
        name
        windows {
          start
          device
          changes
        }
      }
    }
  }
`

// Every site's changes during a day, folded into rows.
export const ACTIVITY_FEED_QUERY = gql`
  query activityFeed(
    $date: String!
    $timeZone: String!
    $actorId: ID
    $kind: ActivityKind
    $search: String
  ) {
    activityFeed(
      date: $date
      timeZone: $timeZone
      actorId: $actorId
      kind: $kind
      search: $search
    ) {
      total
      people {
        id
        name
      }
      groups {
        key
        actorId
        actorName
        firstAt
        lastAt
        total
        user
        kinds
        sites {
          id
          name
        }
        counts {
          action
          count
        }
      }
    }
  }
`
